import { isAxiosError } from 'axios';
import { queryOptions } from '@tanstack/react-query';
import { api } from '@/shared/axios-client';

export interface WebhookConfig {
  isWebhookEnabled: boolean;
  hasSecret?: boolean;
  /** Write-only — sent when saving a new or updated secret. */
  secret?: string | null;
  /** Write-only — true when replacing an existing stored secret. */
  updateSecret?: boolean;
}

/** Normalize API payloads so saved secrets are detected reliably. */
export function normalizeWebhookConfig(
  raw: WebhookConfig | null | undefined,
): WebhookConfig {
  const config = raw ?? { isWebhookEnabled: false };
  return {
    isWebhookEnabled: Boolean(config.isWebhookEnabled),
    hasSecret:
      config.hasSecret === true ||
      Boolean(config.isWebhookEnabled),
  };
}

interface WebhookConfigResponse {
  success: boolean;
  data?: WebhookConfig;
  message?: string;
}

export function getWebhookConfigErrorMessage(
  error: unknown,
  fallback: string,
): string {
  if (isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail)) {
      const message = detail
        .map(item => (typeof item?.msg === 'string' ? item.msg : null))
        .filter(Boolean)
        .join(', ');
      if (message) return message;
    }
    if (error.response?.status === 404) {
      return 'Webhook configuration API was not found. Redeploy the backend with the latest code.';
    }
  }
  return fallback;
}

export const getRepositoryWebhookConfig = async (
  repoId: string,
): Promise<WebhookConfig> => {
  const response = (await api.get(
    `/api/repositories/${encodeURIComponent(repoId)}/webhook-config`,
  )) as WebhookConfigResponse;

  return normalizeWebhookConfig(response.data);
};

export const getWebhookConfigQueryOptions = (repoId: string) =>
  queryOptions({
    queryKey: ['webhook-config', repoId],
    queryFn: () => getRepositoryWebhookConfig(repoId),
    enabled: Boolean(repoId),
    staleTime: 30_000,
    retry: false,
  });

export const saveRepositoryWebhookConfig = async (
  repoId: string,
  config: WebhookConfig,
): Promise<WebhookConfig> => {
  const response = (await api.post(
    `/api/repositories/${encodeURIComponent(repoId)}/webhook-config`,
    config,
  )) as WebhookConfigResponse;

  if (!response.success) {
    throw new Error(response.message || 'Failed to save webhook configuration.');
  }

  return normalizeWebhookConfig(response.data ?? config);
};
